import React from "react";

import "./stylesToggleSwitch.css";

/** Resource identifier handed to the legacy `updateEventCode` callback. */
export type ToggleSwitchResourceId = string | number | object;

/**
 * Visual tone of the slider when the switch is on.
 *
 * - `"primary"` — orange brand gradient (default; matches the 30+ existing
 *   sportsbook / casino consumers).
 * - `"info"` — cyan accent (`#05d2ff`), the active style used by the Casino
 *   Configuration → Games page and adopted by Game Pass.
 *
 * New consumers should pick a tone explicitly; omitting the prop preserves
 * the current orange look so no existing call site needs to change.
 */
export type ToggleSwitchTone = "primary" | "info";

/**
 * Payload shape passed to `updateEventCode` in the default (non-`onlyUpdate`)
 * mode. The field is named `event_code` (snake_case) for backwards compat
 * with the existing 30+ sportsbook / casino consumers and the server-side
 * contract.
 */
export type ToggleSwitchEventPayload = {
  event_code: ToggleSwitchResourceId | undefined;
  is_active: boolean;
};

export type ToggleSwitchProps = {
  /** Form-style controlled value (preferred). antd `Form.Item` injects this. */
  value?: boolean;
  /** Form-style change handler — receives the new boolean. */
  onChange?: (next: boolean) => void;
  /** Legacy controlled state. Used when `value` is not passed. */
  status?: boolean;
  /** Resource identifier handed to `updateEventCode`. Pass any shape your API expects. */
  id?: ToggleSwitchResourceId;
  /** **@deprecated** alias for `id`. Kept for sportsbook composite keys. */
  eventCode?: ToggleSwitchResourceId;
  /**
   * Legacy callback. Receives `{ event_code, is_active }` (default mode) or
   * `data` directly when `onlyUpdate=true`. Typed as `Function` so callers
   * can pass strongly-typed handlers without TS friction at the boundary.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/ban-types
  updateEventCode?: (payload: any) => void;
  /** When true, ignore `id` / `eventCode` and pass `data` straight to `updateEventCode`. */
  onlyUpdate?: boolean;
  /** Required only when `onlyUpdate=true`. Any payload your handler accepts. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
  /** Disables the input and applies muted styles. */
  disabled?: boolean;
  /** Extra className appended to the slider span. */
  sliderClassName?: string;
  /** Visual tone of the active slider. Defaults to the existing `"primary"` orange. */
  tone?: ToggleSwitchTone;
  /**
   * Optional accessible name forwarded to the underlying checkbox input.
   * Needed when the toggle is not accompanied by a native `<label>`
   * association (the internal `<label class="switch">` wraps the slider,
   * not any text). Enables `getByRole("checkbox", { name })` in tests.
   */
  ariaLabel?: string;
};

/**
 * Custom dark-theme toggle switch (no antd dependency).
 *
 * Two API surfaces, fully interoperable:
 *
 * 1. **Form-compatible (preferred)** — `value` + `onChange`. antd `Form.Item`
 *    injects these automatically, so the toggle drops straight into a Form
 *    with no adapter:
 *
 *    ```tsx
 *    <Form.Item name="active" valuePropName="value">
 *      <ToggleSwitch />
 *    </Form.Item>
 *    ```
 *
 *    `valuePropName="value"` is the antd default, so it can usually be
 *    omitted. `onChange` receives the new boolean directly.
 *
 * 2. **Legacy** — `status` + `updateEventCode`. Used widely in the existing
 *    sportsbook / casino config trees. `updateEventCode` receives
 *    `{ event_code, is_active }` (or the raw `data` payload when
 *    `onlyUpdate=true`). Kept fully working — `id` / `eventCode` carry the
 *    resource identifier in this mode.
 *
 * If both APIs are used at the same time, both fire on each toggle.
 */
function ToggleSwitch({
  value,
  onChange,
  status,
  id,
  eventCode,
  updateEventCode,
  onlyUpdate = false,
  data,
  disabled = false,
  sliderClassName = "",
  tone = "primary",
  ariaLabel,
}: ToggleSwitchProps) {
  // Form-style `value` wins over legacy `status` when both are present.
  const checked = value !== undefined ? !!value : !!status;
  const resourceId = id ?? eventCode;

  const handleChangeStatus = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.checked;

    // Form-style — bubble the boolean.
    if (typeof onChange === "function") {
      onChange(next);
    }

    // Legacy callback (still supported alongside Form-style).
    if (typeof updateEventCode === "function") {
      if (!onlyUpdate) {
        const payload: ToggleSwitchEventPayload = {
          event_code: resourceId,
          is_active: next,
        };
        updateEventCode(payload);
      } else {
        updateEventCode(data);
      }
    }
  };

  // Tone is namespaced (`tone-*`) and applied to BOTH the wrapping
  // `<label class="switch">` and the inner `<span class="slider">` so the
  // styles in `stylesToggleSwitch.css` can override either the track colors
  // (via `.slider.tone-*`) or the wrapper dimensions (via `.switch.tone-*`).
  // `"primary"` is intentionally still emitted as a class so the rule set
  // is symmetric and consumers writing their own overrides can target
  // either tone.
  const toneClassName = `tone-${tone}`;

  return (
    <label
      className={`switch ${toneClassName} ${disabled ? "switch-disabled" : ""}`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={handleChangeStatus}
        disabled={disabled}
        aria-label={ariaLabel}
      />
      <span className={`slider round ${toneClassName} ${sliderClassName}`}></span>
    </label>
  );
}

export default ToggleSwitch;
